export const supportedLocales = [
  'en-IN',
  'hi-IN',
  'af-ZA',
  'de-DE',
  'en-AU',
  'en-CA',
  'en-GB',
  'en-US',
  'en-ZA',
  'fr-CA',
  'fr-FR',
  'ja-JP',
  'pt-BR',
] as const;

export type SupportedLocale = (typeof supportedLocales)[number];

export const defaultLocale: SupportedLocale = 'en-IN';

const localePrefixByLocale: Record<SupportedLocale, string> = {
  'en-IN': 'en',
  'hi-IN': 'hi',
  'af-ZA': 'af',
  'de-DE': 'de',
  'en-AU': 'en-au',
  'en-CA': 'en-ca',
  'en-GB': 'en-gb',
  'en-US': 'en-us',
  'en-ZA': 'en-za',
  'fr-CA': 'fr-ca',
  'fr-FR': 'fr',
  'ja-JP': 'ja',
  'pt-BR': 'pt-br',
};

const localeByPrefix = Object.fromEntries(
  Object.entries(localePrefixByLocale).map(([locale, prefix]) => [prefix, locale])
) as Record<string, SupportedLocale>;

const defaultLocaleByLanguage: Record<string, SupportedLocale> = {
  af: 'af-ZA',
  de: 'de-DE',
  en: 'en-IN',
  fr: 'fr-FR',
  hi: 'hi-IN',
  ja: 'ja-JP',
  pt: 'pt-BR',
};

const nonLocalizablePublicPrefixes = [
  '/api',
  '/user',
  '/admin',
  '/og',
  '/assets',
  '/src',
  '/@fs',
  '/@id',
  '/@react-refresh',
  '/@vite',
  '/node_modules',
  '/pwa',
] as const;

const nonLocalizablePublicPaths = new Set([
  '/sitemap.xml',
  '/robots.txt',
  '/llm.txt',
  '/llms.txt',
  '/ai-summary.json',
  '/favicon.ico',
  '/manifest.webmanifest',
  '/sw.js',
]);

function hasFileExtension(pathname: string) {
  return /\.[a-z0-9]+$/i.test(pathname);
}

export function normalizePathname(pathname: string | null | undefined) {
  if (!pathname) {
    return '/';
  }

  let nextPath = pathname.trim();

  if (/^https?:\/\//i.test(nextPath)) {
    try {
      nextPath = new URL(nextPath).pathname;
    } catch {
      nextPath = '/';
    }
  }

  nextPath = nextPath.split('#')[0]?.split('?')[0] || '/';

  if (!nextPath.startsWith('/')) {
    nextPath = `/${nextPath}`;
  }

  nextPath = nextPath.replace(/\/+$/, '');
  return nextPath || '/';
}

export const normalizeSupportedLocale = (
  value: string | null | undefined
): SupportedLocale | null => {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim();

  if (!normalized) {
    return null;
  }

  const exactMatch = supportedLocales.find(locale => locale === normalized);

  if (exactMatch) {
    return exactMatch;
  }

  const normalizedLower = normalized.toLowerCase();
  const localeMatch = supportedLocales.find(locale => locale.toLowerCase() === normalizedLower);

  if (localeMatch) {
    return localeMatch;
  }

  const languagePart = normalizedLower.split(/[-_]/)[0];
  const languageDefault = defaultLocaleByLanguage[languagePart];

  if (languageDefault) {
    return languageDefault;
  }

  const languageMatch = supportedLocales.find(
    locale => locale.toLowerCase().split('-')[0] === languagePart
  );

  return languageMatch ?? null;
};

export function getLocalePrefix(locale: SupportedLocale) {
  return localePrefixByLocale[locale];
}

export function resolveLocaleFromPrefix(prefix: string | null | undefined): SupportedLocale | null {
  if (typeof prefix !== 'string') {
    return null;
  }

  const normalizedPrefix = prefix
    .trim()
    .replace(/^\/+|\/+$/g, '')
    .toLowerCase();

  if (!normalizedPrefix) {
    return null;
  }

  return localeByPrefix[normalizedPrefix] ?? null;
}

export function stripLocalePrefix(pathname: string | null | undefined) {
  const normalizedPath = normalizePathname(pathname);
  const pathSegments = normalizedPath.split('/').filter(Boolean);
  const firstSegment = pathSegments[0] || null;
  const locale = resolveLocaleFromPrefix(firstSegment);

  if (!locale || !firstSegment) {
    return {
      locale: null,
      pathname: normalizedPath,
      hadLocalePrefix: false,
    };
  }

  const remainingSegments = pathSegments.slice(1);
  const strippedPath = remainingSegments.length ? `/${remainingSegments.join('/')}` : '/';

  return {
    locale,
    pathname: strippedPath,
    hadLocalePrefix: true,
  };
}

export function isLocalizablePublicPath(pathname: string | null | undefined) {
  const { pathname: normalizedPath } = stripLocalePrefix(pathname);

  if (nonLocalizablePublicPaths.has(normalizedPath) || normalizedPath.startsWith('/.well-known/')) {
    return false;
  }

  if (
    nonLocalizablePublicPrefixes.some(
      prefix => normalizedPath === prefix || normalizedPath.startsWith(`${prefix}/`)
    )
  ) {
    return false;
  }

  if (normalizedPath !== '/' && hasFileExtension(normalizedPath)) {
    return false;
  }

  return true;
}

export function getLocalizedPublicPath(
  pathname: string | null | undefined,
  locale: SupportedLocale
) {
  const normalizedPath = normalizePathname(pathname);

  if (!isLocalizablePublicPath(normalizedPath)) {
    return stripLocalePrefix(normalizedPath).pathname;
  }

  const basePath = stripLocalePrefix(normalizedPath).pathname;
  const prefix = `/${getLocalePrefix(locale)}`;

  if (basePath === '/') {
    return prefix;
  }

  return `${prefix}${basePath}`;
}

export function getLocalizedPublicAlternates(pathname: string | null | undefined) {
  const basePath = stripLocalePrefix(pathname).pathname;

  return supportedLocales.map(locale => ({
    locale,
    // The existing English pages also serve readers outside India.
    hreflang: locale === 'en-IN' ? 'en' : locale,
    path: getLocalizedPublicPath(basePath, locale),
  }));
}

export const buildLocaleFallbackChain = (locale: string | null | undefined): string[] => {
  const normalized = normalizeSupportedLocale(locale);

  if (!normalized) {
    return [defaultLocale, defaultLocale.split('-')[0]];
  }

  const languagePart = normalized.split('-')[0];
  const fallbacks: string[] = [normalized];

  if (!fallbacks.includes(languagePart)) {
    fallbacks.push(languagePart);
  }

  if (!fallbacks.includes(defaultLocale)) {
    fallbacks.push(defaultLocale);
  }

  const defaultLanguage = defaultLocale.split('-')[0];

  if (!fallbacks.includes(defaultLanguage)) {
    fallbacks.push(defaultLanguage);
  }

  return fallbacks;
};
